import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLostCardStore } from '@lib/store';
import type { GetAllCardsResponse } from '@shared/api';
import { Button, Error, Modal, Radio, Textarea } from '@shared/widgets';

interface IForm {
  type: string;
  comment: string;
}
const DEFAULT_FORM = {
  type: 'Lost',
  comment: '',
};

interface ILostOrStolenModalProps {
  card: GetAllCardsResponse;
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
}

export const LostOrStolenModal = (props: ILostOrStolenModalProps) => {
  const { loading, error, success, create, resetError, clear } = useLostCardStore();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<IForm>({ defaultValues: DEFAULT_FORM });

  useEffect(() => {
    if (loading) return;
    if (error) {
      setError('root', { message: 'Something went wrong. Please try again or contact customer service.' });
      return;
    }
    if (success) {
      clear();
      reset(DEFAULT_FORM);
      props.onClose();
      props.onConfirm?.();
    }
  }, [loading, error, success]);

  const closeHandler = () => {
    resetError();
    reset(DEFAULT_FORM);
    props.onClose();
  };
  const lostOrStolenHandler = (data: IForm) => create(data, props.card.id);

  return (
    <Modal title='Lost or Stolen' isOpen={props.isOpen} onClose={closeHandler}>
      <form onSubmit={handleSubmit(lostOrStolenHandler)}>
        <div className='text-ld max-md:mt-0 max-md:w-auto max-md:font-[Prata]'>
          <div className='font-medium max-md:rounded-lg max-md:bg-[#d7c29a10] max-md:p-2 max-md:text-lg'>
            <span className='max-md:inherit hidden'>Your Card</span>
            XXXX XXXX XXXX {props.card?.cardNumber?.slice(props.card?.cardNumber?.length - 4, props.card?.cardNumber?.length)}
          </div>
          <div className='mt-12 flex flex-col gap-2 max-md:gap-0 max-md:border-t max-md:border-[#4c4c4c10] max-md:p-0'>
            <Controller
              name='type'
              control={control}
              render={({ field }) => (
                <>
                  <Radio
                    className='max-md:border-b max-md:border-b-[#4c4c4c10] max-md:py-4'
                    name={field.name}
                    checked={field.value === 'Lost'}
                    value='Lost'
                    onChange={() => field.onChange('Lost')}
                  >
                    Lost
                  </Radio>
                  <Radio
                    className='max-md:border-b max-md:border-b-[#4c4c4c10] max-md:py-4'
                    name={field.name}
                    checked={field.value === 'Stolen'}
                    value='Stolen'
                    onChange={() => field.onChange('Stolen')}
                  >
                    Stolen
                  </Radio>
                </>
              )}
            />
          </div>
          <Controller
            name='comment'
            control={control}
            render={({ field }) => <Textarea {...field} className='mt-3' label='Leave a comment' cols={40} rows={10} />}
          />
        </div>
        {errors.root?.message && <Error className='mt-6' text={errors.root.message} />}
        <Button className='mx-auto mt-8 w-[302px] max-md:w-full' type='submit' isLoading={loading}>
          Submit
        </Button>
      </form>
    </Modal>
  );
};
